import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchInsights, likeInsight, pinInsight, selectInsights, selectInsightsLoading, selectInsightsError } from '../store/slices/insightSlice';
import InsightList from '../components/organisms/InsightList';
import PageTemplate from '../components/templates/PageTemplate';
import { MobileHeaderType } from '../components/layout/ConfigurableMobileHeader';

const PageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 24px;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ContentCard = styled.div`
  background-color: ${({ theme }) => theme.colors.background.paper};
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 18px;
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const CardDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  line-height: 1.5;
`;

const InsightMetrics = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const MetricCard = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundDark};
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing.md};
  flex: 1;
  text-align: center;
`;

const MetricValue = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 24px;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const MetricLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  width: 100%;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 18px;
`;

const ErrorContainer = styled.div`
  background-color: rgba(255, 0, 0, 0.1);
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: #ff5252;
`;

const Insights: React.FC = () => {
  const dispatch = useAppDispatch();
  const insights = useAppSelector(selectInsights);
  const loading = useAppSelector(selectInsightsLoading);
  const error = useAppSelector(selectInsightsError);

  useEffect(() => {
    dispatch(fetchInsights());
  }, [dispatch]);

  const handleLike = (id: string) => {
    dispatch(likeInsight(id));
  };

  const handleComment = (id: string) => {
    // In a real app, this would open a comment modal
    alert(`Comment functionality for insight ${id} coming soon!`);
  };

  const handlePin = (id: string) => {
    dispatch(pinInsight(id));
  };

  const handleShare = (id: string) => {
    // In a real app, this would open a share dialog
    alert(`Share functionality for insight ${id} coming soon!`);
  };

  return (
    <PageTemplate
      mobileHeaderType={MobileHeaderType.SIMPLE}
      mobileHeaderTitle="Insights"
    >
      <PageContainer>
        <PageTitle>Insights</PageTitle>
        
        {error && (
          <ErrorContainer>
            Error loading insights: {error}
          </ErrorContainer>
        )}
        
        {loading ? (
          <LoadingContainer>
            Loading insights...
          </LoadingContainer>
        ) : (
          <InsightList 
            insights={insights}
            onLike={handleLike}
            onComment={handleComment}
            onPin={handlePin}
            onShare={handleShare}
          />
        )}
      </PageContainer>
    </PageTemplate>
  );
};

export default Insights;
